import { Pagination } from '@patternfly/react-core'
import { TableComposable, TableVariant, Tbody } from '@patternfly/react-table'
import { useCallback } from 'react'
import * as React from 'react'

import type { IPatient } from '@app/domain/patient/patient.interface'
import type { IPagination} from '@app/lib/pagination';
import { useHandleCurrentPageChanged } from '@app/lib/pagination'
import type { ISorting } from '@app/lib/sorting'

import { ColumnHeader, PatientListHeader } from './patient-list-header'
import { PatientListRow } from './patient-list-row'

import type { PatientActionItem } from './patient-list.interface'

interface Props {
  buildActions?: (patient: IPatient) => PatientActionItem[]
  buildColumns?: (patient: IPatient) => React.ReactElement[]
  columnHeaders: ColumnHeader[]
  onActionSelected?: (key: string, patient: IPatient) => void
  onPatientSelected: (patient: IPatient) => void
  pagination: IPagination
  patients: Array<IPatient>
  selectedPatients?: IPatient[]
  sorting: ISorting
}

export const PopulatedPatientList = ({
  buildActions,
  buildColumns,
  columnHeaders,
  onActionSelected,
  onPatientSelected,
  patients,
  pagination,
  selectedPatients,
  sorting,
}: Props) => {
  const {
    pageNumber,
    onSetPage,
    onPerPageSelect,
    totalNumberOfRecords,
    recordsPerPage,
  } = pagination

  const handleSetPage = useCallback(
    (_event, updatedPageNumber) => {
      onSetPage(updatedPageNumber)
    },
    [onSetPage],
  )

  const handlePerPageSelect = useCallback(
    (_event, updatedPerPage) => {
      onPerPageSelect(updatedPerPage)
    },
    [onPerPageSelect],
  )

  const { handleCurrentPageChanged } = useHandleCurrentPageChanged(
    onSetPage,
    recordsPerPage,
    totalNumberOfRecords,
  )

  return (
    <>
      <TableComposable
        variant={TableVariant.compact}
        aria-label="Clinic Patients"
      >
        <PatientListHeader
          columnHeaders={columnHeaders}
          sorting={sorting}
          addActionColumn={buildActions !== undefined}
        />
        <Tbody>
          {patients.map((patient, idx) => {
            const actions = buildActions?.(patient) ?? []
            const columns = buildColumns?.(patient) ?? []
            return (
              <PatientListRow
                actions={actions}
                columns={columns}
                onActionSelected={onActionSelected}
                onPatientSelected={onPatientSelected}
                isSelected={selectedPatients?.includes(patient)}
                patient={patient}
                key={idx}
              />
            )
          })}
        </Tbody>
      </TableComposable>
      <Pagination
        itemCount={totalNumberOfRecords}
        onBlur={handleCurrentPageChanged}
        onChange={handleCurrentPageChanged}
        onPerPageSelect={handlePerPageSelect}
        onSetPage={handleSetPage}
        page={pageNumber}
        perPage={recordsPerPage}
        widgetId="pagination-options-menu-bottom"
      />
    </>
  )
}
